import { useCallback, useEffect, useRef, useState } from 'react';

/** The person closed the reason prompt; the save that asked for it does not run. */
export class VerifiedEditReasonCancelledError extends Error {
  constructor() {
    super('A reason is required to change a verified intake');
    this.name = 'VerifiedEditReasonCancelledError';
  }
}

interface PendingPrompt {
  promise: Promise<string>;
  resolve: (reason: string) => void;
  reject: (error: Error) => void;
}

/**
 * Collect the reason the API requires before a verified intake is edited
 * (GDN-4034). Editing is never blocked: saves wait on one shared prompt, and
 * the submitted reason is reused by every later save until `clear` runs.
 * Providers keep it for the session; admins clear it after each Save changes.
 */
export function useVerifiedEditReason(isVerified: boolean) {
  const isVerifiedRef = useRef(isVerified);
  const reasonRef = useRef<string | undefined>(undefined);
  const pendingRef = useRef<PendingPrompt | null>(null);
  const unmountedRef = useRef(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    isVerifiedRef.current = isVerified;
    // A reason belongs to one verification: once the intake is unverified, a
    // later re-verification must ask again rather than reuse it.
    if (!isVerified) reasonRef.current = undefined;
  }, [isVerified]);

  // Settle any save still waiting on the prompt when the editor goes away, so
  // callers do not hang on a dialog that can no longer open.
  useEffect(() => {
    // StrictMode rehearses this cleanup on a still-mounted editor.
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      pendingRef.current?.reject(new VerifiedEditReasonCancelledError());
      pendingRef.current = null;
    };
  }, []);

  // `force` asks even when this page does not know the intake is verified: the
  // API is the authority, and it has just said a reason is required.
  const resolveReason = useCallback((force = false): Promise<string | undefined> => {
    if (!isVerifiedRef.current && !force) return Promise.resolve(undefined);
    if (reasonRef.current) return Promise.resolve(reasonRef.current);
    // A write that fails after the editor is gone has no dialog to ask with.
    if (unmountedRef.current) return Promise.reject(new VerifiedEditReasonCancelledError());
    if (!pendingRef.current) {
      let resolve!: (reason: string) => void;
      let reject!: (error: Error) => void;
      const promise = new Promise<string>((resolvePromise, rejectPromise) => {
        resolve = resolvePromise;
        reject = rejectPromise;
      });
      // Callers await this promise; the no-op handler only stops a prompt that
      // is settled on unmount from surfacing as an unhandled rejection.
      promise.catch(() => undefined);
      pendingRef.current = { promise, resolve, reject };
      setOpen(true);
    }
    return pendingRef.current.promise;
  }, []);

  const onSubmit = useCallback((reason: string) => {
    const cleaned = reason.trim();
    if (!cleaned) return;
    // Only a known verification keeps the reason; a forced one covers just
    // the retry, since nothing would clear it before a later re-verification.
    if (isVerifiedRef.current) reasonRef.current = cleaned;
    pendingRef.current?.resolve(cleaned);
    pendingRef.current = null;
    setOpen(false);
  }, []);

  const onCancel = useCallback(() => {
    pendingRef.current?.reject(new VerifiedEditReasonCancelledError());
    pendingRef.current = null;
    setOpen(false);
  }, []);

  const clear = useCallback(() => {
    reasonRef.current = undefined;
  }, []);

  /**
   * Run a write with any reason already needed. If the API still rejects it
   * for a missing reason, ask once and retry, so a save never loops on a
   * failure the person cannot see or fix.
   */
  const withReason = useCallback(
    async <T,>(write: (body: VerifiedEditReasonBody) => Promise<T>): Promise<T> => {
      const reason = await resolveReason();
      try {
        return await write(verifiedEditReasonBody(reason));
      } catch (error) {
        if (reason || !isVerifiedEditReasonRequired(error)) throw error;
        return write(verifiedEditReasonBody(await resolveReason(true)));
      }
    },
    [resolveReason],
  );

  return { resolveReason, withReason, clear, dialog: { open, onSubmit, onCancel } };
}

type VerifiedEditReasonBody = { verified_edit_reason?: string };

/** The API refused a write to a verified intake because it carried no reason. */
export function isVerifiedEditReasonRequired(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const data = (error as { data?: { error_code?: unknown } }).data;
  return data?.error_code === 'verified_edit_reason_required';
}

/** Request-body fragment for a resolved reason; empty when none is needed. */
export function verifiedEditReasonBody(reason: string | undefined): VerifiedEditReasonBody {
  return reason ? { verified_edit_reason: reason } : {};
}
